import { useAuthStore } from '@/lib/auth/authStore';

/**
 * Convert PascalCase entity name (e.g. "XpProject") to the kebab-case key
 * the backend uses in the JWT permissions map (e.g. "xp-project").
 */
function toKebab(entity: string): string {
  return entity.replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`).replace(/^-/, '');
}

/**
 * Check whether the authenticated user has `action` on `entity`.
 *
 * The JWT permissions map already expands `full` into the full action list,
 * so a simple `actions.includes(action)` works for every action including
 * `full` itself.
 *
 * Onxite global admins are matched via the `onxite` entity key (any action)
 * — `getUserPermissions` on the backend grants them everything anyway, so
 * this is a no-op for them.
 */
export function canAccess(
  permissions: Record<string, string[]>,
  entity: string,
  action: string,
): boolean {
  const key = toKebab(entity);
  const actions = permissions[key] ?? [];
  return actions.includes(action) || actions.includes('full');
}

/**
 * AND-combinator: returns true only if ALL the requested (entity, action)
 * pairs are satisfied.
 */
export function canAccessAll(
  permissions: Record<string, string[]>,
  pairs: Array<[entity: string, action: string]>,
): boolean {
  return pairs.every(([e, a]) => canAccess(permissions, e, a));
}

/**
 * OR-combinator: true if ANY of the (entity, action) pairs is satisfied.
 */
export function canAccessAny(
  permissions: Record<string, string[]>,
  pairs: Array<[entity: string, action: string]>,
): boolean {
  return pairs.some(([e, a]) => canAccess(permissions, e, a));
}

/**
 * View-grade permission. True when the user can either `list` or `show` the
 * entity (or has `full`, which is auto-expanded by `canAccess`).
 */
export function canView(
  permissions: Record<string, string[]>,
  entity: string,
): boolean {
  return canAccess(permissions, entity, 'list') || canAccess(permissions, entity, 'show');
}

/** Hook version — re-renders when permissions change. */
export function useCanAccess(entity: string, action: string): boolean {
  const permissions = useAuthStore((s) => s.permissions);
  return canAccess(permissions, entity, action);
}

/** Hook AND-combinator. */
export function useCanAccessAll(
  pairs: Array<[entity: string, action: string]>,
): boolean {
  const permissions = useAuthStore((s) => s.permissions);
  return canAccessAll(permissions, pairs);
}

/** Hook OR-combinator. */
export function useCanAccessAny(
  pairs: Array<[entity: string, action: string]>,
): boolean {
  const permissions = useAuthStore((s) => s.permissions);
  return canAccessAny(permissions, pairs);
}

/** Hook view-grade permission (list || show || full). */
export function useCanView(entity: string): boolean {
  const permissions = useAuthStore((s) => s.permissions);
  return canView(permissions, entity);
}

/**
 * Returns true if the user can open the Access Manager. They need view-grade
 * access to AT LEAST ONE surface (Users, Roles, or Grants) — otherwise the
 * menu and the page are hidden entirely. Each tab is then gated independently
 * inside the page.
 */
export function useIsAccessManager(): boolean {
  return useCanAccessAny([
    ['User', 'list'],
    ['User', 'show'],
    ['Role', 'list'],
    ['Role', 'show'],
    ['ExternalAccessGrant', 'list'],
    ['ExternalAccessGrant', 'show'],
  ]);
}

/**
 * Can compose and send notifications. Receiving + marking-as-read does NOT
 * require this permission — every authenticated user can do that.
 */
export function useCanSendNotifications(): boolean {
  return useCanAccess('Notification', 'create');
}
